import React, { useState } from 'react';
import { Product, Slot, DateGroup, SettingsData } from '../types';

interface SettingsViewProps {
  initialSettings: SettingsData;
  onClose: () => void;
  onSave: (data: SettingsData) => void;
}

// 模拟商品库
const MOCK_PRODUCTS: Product[] = [
  { id: 'p1', name: '工作日冰室自助', price: '68.00', image: 'https://images.unsplash.com/photo-1544145945-f904253db0ad?w=100&h=100&fit=crop' },
  { id: 'p2', name: '周末冰室自助', price: '78.00', image: 'https://images.unsplash.com/photo-1551024601-bec78aea704b?w=100&h=100&fit=crop' },
  { id: 'p3', name: '工作日甜品自助', price: '68.00', image: 'https://images.unsplash.com/photo-1563729784474-d77dbb933a9e?w=100&h=100&fit=crop' },
  { id: 'p4', name: '周末甜品自助', price: '78.00', image: 'https://images.unsplash.com/photo-1488477181946-6428a0291777?w=100&h=100&fit=crop' },
];

const DAY_NAMES = ['周一', '周二', '周三', '周四', '周五', '周六', '周日'];

const PRESET_SLOTS = [
  { name: '上午场', time: '10:00-12:00' },
  { name: '中午场', time: '12:00-14:00' },
  { name: '下午场', time: '14:00-17:00' },
  { name: '晚间场', time: '17:00-21:00' },
  { name: '全天场', time: '10:00-22:00' },
];

const BRAND_DEFAULT_DATE_GROUPS: DateGroup[] = [
  {
    id: 'brand-group-1',
    days: [1, 2, 3, 4, 5],
    slots: [
      { id: 'bs1', name: '上午场', time: '10:00-12:00', maxCapacity: 20, product: MOCK_PRODUCTS[0] },
      { id: 'bs2', name: '下午场', time: '14:00-17:00', maxCapacity: 20, product: MOCK_PRODUCTS[2] },
    ]
  },
  {
    id: 'brand-group-2',
    days: [6, 7],
    slots: [
      { id: 'bs3', name: '全天场', time: '10:00-22:00', maxCapacity: 30, product: MOCK_PRODUCTS[1] },
    ]
  }
];

const SettingsView: React.FC<SettingsViewProps> = ({ initialSettings, onClose, onSave }) => {
  const [mode, setMode] = useState<'brand' | 'custom'>(initialSettings.mode);
  const [isModePickerOpen, setIsModePickerOpen] = useState(false);
  const [dateGroups, setDateGroups] = useState<DateGroup[]>(initialSettings.dateGroups || []);
  
  const [selectingProductIdForSlot, setSelectingProductIdForSlot] = useState<{groupId: string, slotId: string} | null>(null);
  const [selectingSlotFor, setSelectingSlotFor] = useState<{groupId: string, slotId: string} | null>(null);

  const [confirmDelete, setConfirmDelete] = useState<{
    type: 'group' | 'slot';
    groupId: string;
    slotId?: string;
  } | null>(null);

  const handleSave = () => {
    onSave({ mode, dateGroups });
  };

  const handleAddDateGroup = () => {
    const newGroup: DateGroup = {
      id: Math.random().toString(36).substr(2, 9),
      days: [],
      slots: []
    };
    setDateGroups([...dateGroups, newGroup]);
  };

  const handleRemoveDateGroup = (groupId: string) => {
    setConfirmDelete({ type: 'group', groupId });
  };

  const handleToggleDay = (groupId: string, day: number) => {
    setDateGroups(dateGroups.map(g => {
      if (g.id === groupId) {
        const newDays = g.days.includes(day) 
          ? g.days.filter(d => d !== day) 
          : [...g.days, day].sort((a, b) => a - b);
        return { ...g, days: newDays };
      }
      return g;
    }));
  };

  const handleAddSlot = (groupId: string) => {
    setDateGroups(dateGroups.map(g => {
      if (g.id === groupId) {
        if (g.slots.length >= 10) {
          alert('已达场次配置上限（10个）');
          return g;
        }
        const newSlot: Slot = {
          id: Math.random().toString(36).substr(2, 9),
          name: '',
          time: '',
          maxCapacity: 20,
          product: null,
        };
        return { ...g, slots: [...g.slots, newSlot] };
      }
      return g;
    }));
  };

  const handleCapacityChange = (groupId: string, slotId: string, delta: number) => {
    setDateGroups(dateGroups.map(g => {
      if (g.id === groupId) {
        return {
          ...g,
          slots: g.slots.map(s => s.id === slotId ? { ...s, maxCapacity: Math.max(0, s.maxCapacity + delta) } : s)
        };
      }
      return g;
    }));
  };

  const handleNameChange = (groupId: string, slotId: string, newName: string) => {
    setDateGroups(dateGroups.map(g => {
      if (g.id === groupId) {
        return {
          ...g,
          slots: g.slots.map(s => s.id === slotId ? { ...s, name: newName } : s)
        };
      }
      return g;
    }));
  };

  const handleTimeChange = (groupId: string, slotId: string, newTime: string) => {
    setDateGroups(dateGroups.map(g => {
      if (g.id === groupId) {
        return {
          ...g,
          slots: g.slots.map(s => s.id === slotId ? { ...s, time: newTime } : s)
        };
      }
      return g;
    }));
  };

  const handleRemoveSlot = (groupId: string, slotId: string) => {
    setConfirmDelete({ type: 'slot', groupId, slotId });
  };

  const handleSelectProduct = (product: Product) => {
    if (!selectingProductIdForSlot) return;
    const { groupId, slotId } = selectingProductIdForSlot;
    setDateGroups(dateGroups.map(g => {
      if (g.id === groupId) {
        return {
          ...g,
          slots: g.slots.map(s => s.id === slotId ? { ...s, product } : s)
        };
      }
      return g;
    }));
    setSelectingProductIdForSlot(null);
  };

  const handleSelectPresetSlot = (preset: {name: string, time: string}) => {
    if (!selectingSlotFor) return;
    const { groupId, slotId } = selectingSlotFor;
    setDateGroups(dateGroups.map(g => {
      if (g.id === groupId) {
        return {
          ...g,
          slots: g.slots.map(s => s.id === slotId ? { ...s, name: preset.name, time: preset.time } : s)
        };
      }
      return g;
    }));
    setSelectingSlotFor(null);
  };

  return (
    <div className="absolute inset-0 z-[600] bg-[#F4F5F7] flex flex-col animate-in slide-in-from-right duration-300 overflow-hidden">
      {/* 顶部标题栏 */}
      <header className="bg-white px-4 h-14 flex items-center justify-between shrink-0 border-b border-gray-100/50">
        <button onClick={onClose} className="w-10 h-10 flex items-center justify-center text-gray-800 active:bg-gray-50 rounded-full transition-all">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h2 className="text-[17px] font-black text-gray-900 tracking-tight">预约设置</h2>
        <div className="w-10"></div>
      </header>

      <div className="flex-1 overflow-y-auto no-scrollbar pb-40">
        {/* 预约模式：下拉卡片样式 */}
        <section className="px-5 pt-6 space-y-3">
          <label className="text-[12px] font-black text-gray-400 uppercase tracking-widest pl-1">预约模式</label>
          <div className="relative group">
            <button 
              onClick={() => setIsModePickerOpen(true)}
              className="w-full h-14 bg-white border border-gray-100 rounded-3xl px-6 flex items-center justify-between text-[15px] font-black text-gray-900 shadow-[0_2px_8px_rgba(0,0,0,0.02)] outline-none focus:ring-2 focus:ring-[#5DA756]/10 transition-all cursor-pointer active:bg-gray-50"
            >
              <span>{mode === 'brand' ? '与品牌一致' : '单独设置'}</span>
              <div className="text-gray-300 group-hover:text-[#5DA756] transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" /></svg>
              </div>
            </button>
          </div>
        </section>

        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 px-5 pt-8 pb-12 space-y-6">
          {(mode === 'custom' ? dateGroups : BRAND_DEFAULT_DATE_GROUPS).map((group, groupIndex) => (
            <div key={group.id} className="bg-white rounded-3xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-white/50 space-y-6 animate-in zoom-in-95 duration-300">
              {/* 选择日期 */}
              <div>
                <div className="flex items-center justify-between mb-4 px-1">
                  <label className="text-[13px] font-black text-gray-900 flex items-center">
                    <span className="text-red-500 mr-1 text-[16px] leading-none">*</span>选择日期
                  </label>
                  {mode === 'custom' && dateGroups.length > 1 && (
                    <button onClick={() => handleRemoveDateGroup(group.id)} className="text-[12px] font-bold text-red-400 hover:text-red-500 active:scale-95 transition-all">
                      删除
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {[1, 2, 3, 4, 5, 6, 7].map(day => {
                    const isSelected = group.days.includes(day);
                    const isUsedByOther = (mode === 'custom' ? dateGroups : BRAND_DEFAULT_DATE_GROUPS).some(g => g.id !== group.id && g.days.includes(day));
                    
                    return (
                      <button
                        key={day}
                        onClick={() => mode === 'custom' && !isUsedByOther && handleToggleDay(group.id, day)}
                        disabled={mode === 'brand' || isUsedByOther}
                        className={`
                          flex items-center space-x-1.5 px-3.5 py-2 rounded-xl text-[13px] font-bold transition-all border
                          ${isSelected ? 'bg-[#EEF5ED] border-[#5DA756] text-[#5DA756] shadow-sm' : 
                            isUsedByOther ? 'bg-gray-50/50 border-gray-100 text-gray-300 cursor-not-allowed' : 
                            'bg-white border-gray-200 text-gray-600 hover:border-gray-300 active:bg-gray-50'}
                          ${mode === 'brand' ? 'opacity-80 cursor-default' : ''}
                        `}
                      >
                        <div className={`w-4 h-4 rounded-[4px] border flex items-center justify-center transition-colors
                          ${isSelected ? 'bg-[#5DA756] border-[#5DA756]' : 
                            isUsedByOther ? 'bg-gray-100 border-gray-200' : 
                            'bg-white border-gray-300'}
                        `}>
                          {isSelected && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7" /></svg>}
                        </div>
                        <span>{DAY_NAMES[day - 1]}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* 场次列表 */}
              <div className="space-y-4 pt-2 border-t border-gray-100/80">
                <div className="flex items-center justify-between px-1">
                  <label className="text-[12px] font-black text-gray-400 uppercase tracking-widest">场次设置</label>
                  <div className="bg-gray-100 px-2 py-0.5 rounded-full">
                    <span className="text-[10px] font-black text-gray-400">{group.slots.length} / 10</span>
                  </div>
                </div>

                {group.slots.length === 0 ? (
                  <div className="py-8 flex flex-col items-center justify-center text-gray-300 space-y-3 bg-gray-50/50 rounded-2xl border border-dashed border-gray-200">
                    <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <p className="text-[13px] font-bold">暂无预约场次</p>
                  </div>
                ) : (
                  group.slots.map((slot, index) => (
                    <div key={slot.id} className="bg-gray-50/50 rounded-2xl p-5 border border-gray-100/80 space-y-5 relative">
                      {/* 删除按钮 */}
                      {mode === 'custom' && (
                        <button onClick={() => handleRemoveSlot(group.id, slot.id)} className="absolute top-4 right-4 text-red-400 hover:text-red-500 active:scale-90 transition-all">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      )}

                      {/* 场次名称和时间 */}
                      <div className="flex items-center justify-between px-1 pt-2">
                        <span className="text-[13px] font-black text-gray-600">选择场次：</span>
                        {slot.name ? (
                          <button 
                            onClick={() => mode === 'custom' && setSelectingSlotFor({groupId: group.id, slotId: slot.id})}
                            disabled={mode === 'brand'}
                            className={`flex items-center space-x-2 text-[13px] font-bold text-[#5DA756] transition-opacity ${mode === 'brand' ? 'cursor-default opacity-80' : 'active:opacity-70'}`}
                          >
                            <span className="truncate max-w-[150px]">{slot.name} {slot.time}</span>
                            {mode === 'custom' && <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>}
                          </button>
                        ) : (
                          <button 
                            onClick={() => mode === 'custom' && setSelectingSlotFor({groupId: group.id, slotId: slot.id})}
                            disabled={mode === 'brand'}
                            className={`flex items-center space-x-1 text-[13px] font-bold text-gray-400 transition-opacity ${mode === 'brand' ? 'cursor-default opacity-80' : 'active:opacity-70'}`}
                          >
                            <span>请选择</span>
                            {mode === 'custom' && <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>}
                          </button>
                        )}
                      </div>

                      {/* 预约限额 */}
                      <div className="flex items-center justify-between px-1">
                        <span className="text-[13px] font-black text-gray-600">最多预约数量：</span>
                        <div className={`flex items-center bg-white rounded-xl border border-gray-200/60 shadow-sm h-10 overflow-hidden ${mode === 'brand' ? 'opacity-80' : ''}`}>
                          {mode === 'custom' && <button onClick={() => handleCapacityChange(group.id, slot.id, -1)} className="w-10 h-full flex items-center justify-center text-gray-500 font-black text-lg active:bg-gray-50 transition-colors hover:text-[#5DA756]">-</button>}
                          {mode === 'custom' && <div className="w-px h-4 bg-gray-200"></div>}
                          <span className={`text-center text-[14px] font-black text-gray-900 ${mode === 'brand' ? 'w-20' : 'w-14'}`}>{slot.maxCapacity}</span>
                          {mode === 'custom' && <div className="w-px h-4 bg-gray-200"></div>}
                          {mode === 'custom' && <button onClick={() => handleCapacityChange(group.id, slot.id, 1)} className="w-10 h-full flex items-center justify-center text-gray-500 font-black text-lg active:bg-gray-50 transition-colors hover:text-[#5DA756]">+</button>}
                        </div>
                      </div>

                      {/* 关联商品 */}
                      <div className="flex items-center justify-between px-1">
                        <span className="text-[13px] font-black text-gray-600">关联商品：</span>
                        {slot.product ? (
                          <button 
                            onClick={() => mode === 'custom' && setSelectingProductIdForSlot({groupId: group.id, slotId: slot.id})}
                            disabled={mode === 'brand'}
                            className={`flex items-center space-x-2 text-[13px] font-bold text-[#5DA756] transition-opacity ${mode === 'brand' ? 'cursor-default opacity-80' : 'active:opacity-70'}`}
                          >
                            <span className="truncate max-w-[150px]">{slot.product.name}</span>
                            {mode === 'custom' && <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>}
                          </button>
                        ) : (
                          <button 
                            onClick={() => mode === 'custom' && setSelectingProductIdForSlot({groupId: group.id, slotId: slot.id})}
                            disabled={mode === 'brand'}
                            className={`flex items-center space-x-1 text-[13px] font-bold text-gray-400 transition-opacity ${mode === 'brand' ? 'cursor-default opacity-80' : 'active:opacity-70'}`}
                          >
                            <span>请选择</span>
                            {mode === 'custom' && <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>}
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}

                {/* 新增场次按钮 */}
                {mode === 'custom' && group.slots.length < 10 && (
                  <button 
                    onClick={() => handleAddSlot(group.id)}
                    className="w-full py-4 border-2 border-dashed border-gray-200 rounded-2xl flex items-center justify-center text-gray-400 space-x-2 active:bg-white active:border-[#5DA756] active:text-[#5DA756] transition-all group"
                  >
                    <svg className="w-5 h-5 group-active:text-[#5DA756]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                    <span className="text-[14px] font-bold">新增场次 <span className="text-[12px] font-normal opacity-70">最多支持10个</span></span>
                  </button>
                )}
              </div>
            </div>
          ))}

          {/* 新增日期组按钮 */}
          {mode === 'custom' && (
            <button 
              onClick={handleAddDateGroup}
              className="w-full py-5 border-2 border-dashed border-[#5DA756]/30 bg-[#F6FBF6] rounded-3xl flex items-center justify-center text-[#5DA756] space-x-2 active:scale-[0.98] transition-all"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
              <span className="text-[15px] font-black">新增日期</span>
            </button>
          )}
        </div>
      </div>

      {/* 底部悬浮操作区 */}
      {mode === 'custom' && (
        <footer className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-[#F4F5F7] via-[#F4F5F7] to-transparent pt-12 z-[500] pointer-events-none">
          <div className="flex space-x-4 pointer-events-auto">
            <button onClick={onClose} className="flex-1 h-14 bg-white border border-gray-100 rounded-[1.25rem] text-[15px] font-black text-gray-400 shadow-xl active:bg-gray-50 active:scale-95 transition-all">取消修改</button>
            <button onClick={handleSave} className="flex-[1.5] h-14 bg-[#1C1C1E] text-white rounded-[1.25rem] text-[15px] font-black shadow-2xl shadow-black/20 active:scale-[0.98] transition-all">保存设置</button>
          </div>
        </footer>
      )}

      {/* 商品选择层 */}
      {selectingProductIdForSlot && (
        <div className="absolute inset-0 z-[1000] animate-in fade-in duration-200">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-[3px]" onClick={() => setSelectingProductIdForSlot(null)}></div>
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-[3.5rem] shadow-[0_-20px_60px_rgba(0,0,0,0.15)] flex flex-col max-h-[88%] animate-in slide-in-from-bottom duration-400 overflow-hidden">
            <div className="w-12 h-1.5 bg-gray-100 rounded-full mx-auto mt-4 mb-2"></div>
            <header className="px-8 py-6 flex items-center justify-between">
              <h3 className="text-[19px] font-black text-gray-900 tracking-tight">选择关联商品</h3>
              <button onClick={() => setSelectingProductIdForSlot(null)} className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-full text-gray-400 active:scale-90 transition-all">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </header>
            
            <div className="flex-1 overflow-y-auto px-6 space-y-4 pb-40 no-scrollbar">
              {MOCK_PRODUCTS.map(product => {
                const currentGroup = dateGroups.find(g => g.id === selectingProductIdForSlot.groupId);
                const currentSlot = currentGroup?.slots.find(s => s.id === selectingProductIdForSlot.slotId);
                const isSelected = currentSlot?.product?.id === product.id;
                return (
                  <div 
                    key={product.id} 
                    onClick={() => handleSelectProduct(product)}
                    className={`flex items-center p-5 rounded-3xl border-2 transition-all cursor-pointer ${isSelected ? 'bg-[#F6FBF6] border-[#5DA756] shadow-lg shadow-[#5DA756]/5' : 'bg-[#F9FAFB] border-transparent active:border-gray-200 active:bg-white'}`}
                  >
                    <div className="relative">
                      <img src={product.image} className="w-16 h-16 rounded-2xl object-cover shadow-sm ring-2 ring-white" alt="" />
                      {isSelected && <div className="absolute -top-1 -right-1 w-4 h-4 bg-[#5DA756] rounded-full ring-2 ring-white"></div>}
                    </div>
                    <div className="flex-1 min-w-0 ml-5">
                      <p className="text-[15px] font-black text-gray-900 mb-1">{product.name}</p>
                      <p className="text-[13px] font-black text-[#5DA756]">¥ {product.price}</p>
                    </div>
                    <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all ${isSelected ? 'border-[#5DA756] bg-[#5DA756] scale-110 shadow-md shadow-[#5DA756]/20' : 'border-gray-200 bg-white'}`}>
                      {isSelected && <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7" /></svg>}
                    </div>
                  </div>
                );
              })}
            </div>

            <footer className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-white via-white/95 to-transparent pt-16">
              <button 
                onClick={() => setSelectingProductIdForSlot(null)}
                className="w-full h-15 bg-[#5DA756] text-white rounded-2xl text-[16px] font-black shadow-2xl shadow-[#5DA756]/30 active:scale-[0.97] transition-all py-4"
              >
                确认选择
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* 预约模式选择层 */}
      {isModePickerOpen && (
        <div className="absolute inset-0 z-[1000] animate-in fade-in duration-200">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-[3px]" onClick={() => setIsModePickerOpen(false)}></div>
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-[3.5rem] shadow-[0_-20px_60px_rgba(0,0,0,0.15)] flex flex-col animate-in slide-in-from-bottom duration-400 overflow-hidden">
            <div className="w-12 h-1.5 bg-gray-100 rounded-full mx-auto mt-4 mb-2"></div>
            <header className="px-8 py-6 flex items-center justify-between">
              <h3 className="text-[19px] font-black text-gray-900 tracking-tight">选择预约模式</h3>
              <button onClick={() => setIsModePickerOpen(false)} className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-full text-gray-400 active:scale-90 transition-all">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </header>
            
            <div className="flex-1 px-6 pb-12 space-y-3">
              {[
                { value: 'brand', label: '与品牌一致' },
                { value: 'custom', label: '单独设置' }
              ].map((option) => {
                const isSelected = mode === option.value;
                return (
                  <div 
                    key={option.value} 
                    onClick={() => {
                      const newMode = option.value as 'brand' | 'custom';
                      setMode(newMode);
                      setIsModePickerOpen(false);
                      // 如果切换到品牌一致，直接保存并关闭（因为底部按钮被隐藏了）
                      if (newMode === 'brand') {
                        onSave({ mode: 'brand', dateGroups });
                      }
                    }}
                    className={`flex items-center p-5 rounded-3xl border-2 transition-all cursor-pointer ${isSelected ? 'bg-[#F6FBF6] border-[#5DA756] shadow-lg shadow-[#5DA756]/5' : 'bg-[#F9FAFB] border-transparent active:border-gray-200 active:bg-white'}`}
                  >
                    <div className="flex-1 min-w-0 ml-2">
                      <p className={`text-[16px] font-black mb-1 ${isSelected ? 'text-[#5DA756]' : 'text-gray-900'}`}>{option.label}</p>
                    </div>
                    <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all ${isSelected ? 'border-[#5DA756] bg-[#5DA756] scale-110 shadow-md shadow-[#5DA756]/20' : 'border-gray-200 bg-white'}`}>
                      {isSelected && <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7" /></svg>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* 场次选择层 */}
      {selectingSlotFor && (
        <div className="absolute inset-0 z-[1000] animate-in fade-in duration-200">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-[3px]" onClick={() => setSelectingSlotFor(null)}></div>
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-[3.5rem] shadow-[0_-20px_60px_rgba(0,0,0,0.15)] flex flex-col max-h-[88%] animate-in slide-in-from-bottom duration-400 overflow-hidden">
            <div className="w-12 h-1.5 bg-gray-100 rounded-full mx-auto mt-4 mb-2"></div>
            <header className="px-8 py-6 flex items-center justify-between">
              <h3 className="text-[19px] font-black text-gray-900 tracking-tight">选择场次</h3>
              <button onClick={() => setSelectingSlotFor(null)} className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-full text-gray-400 active:scale-90 transition-all">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </header>
            
            <div className="flex-1 overflow-y-auto px-6 space-y-3 pb-40 no-scrollbar">
              {PRESET_SLOTS.map((preset, index) => {
                const currentGroup = dateGroups.find(g => g.id === selectingSlotFor.groupId);
                const currentSlot = currentGroup?.slots.find(s => s.id === selectingSlotFor.slotId);
                const isSelected = currentSlot?.name === preset.name && currentSlot?.time === preset.time;
                return (
                  <div 
                    key={index} 
                    onClick={() => handleSelectPresetSlot(preset)}
                    className={`flex items-center p-5 rounded-3xl border-2 transition-all cursor-pointer ${isSelected ? 'bg-[#F6FBF6] border-[#5DA756] shadow-lg shadow-[#5DA756]/5' : 'bg-[#F9FAFB] border-transparent active:border-gray-200 active:bg-white'}`}
                  >
                    <div className="flex-1 min-w-0 ml-2">
                      <p className="text-[16px] font-black text-gray-900 mb-1">{preset.name}</p>
                      <p className="text-[13px] font-bold text-gray-500">{preset.time}</p>
                    </div>
                    <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all ${isSelected ? 'border-[#5DA756] bg-[#5DA756] scale-110 shadow-md shadow-[#5DA756]/20' : 'border-gray-200 bg-white'}`}>
                      {isSelected && <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7" /></svg>}
                    </div>
                  </div>
                );
              })}
            </div>

            <footer className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-white via-white/95 to-transparent pt-16">
              <button 
                onClick={() => setSelectingSlotFor(null)}
                className="w-full h-15 bg-[#5DA756] text-white rounded-2xl text-[16px] font-black shadow-2xl shadow-[#5DA756]/30 active:scale-[0.97] transition-all py-4"
              >
                确认选择
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* 二次确认弹窗 */}
      {confirmDelete && (
        <div className="absolute inset-0 z-[2000] flex items-center justify-center p-6 animate-in fade-in duration-200">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-[4px]" onClick={() => setConfirmDelete(null)}></div>
          <div className="bg-white w-full max-w-[320px] rounded-[2.5rem] p-8 shadow-2xl relative animate-in zoom-in-95 duration-300 text-center">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </div>
            <h3 className="text-[18px] font-black text-gray-900 mb-2">确认删除？</h3>
            <p className="text-[14px] font-bold text-gray-400 mb-8 leading-relaxed">
              {confirmDelete.type === 'group' ? '删除后该日期组的所有配置将无法恢复' : '删除后该场次配置将无法恢复'}
            </p>
            <div className="space-y-3">
              <button 
                onClick={() => {
                  if (confirmDelete.type === 'group') {
                    setDateGroups(dateGroups.filter(g => g.id !== confirmDelete.groupId));
                  } else {
                    setDateGroups(dateGroups.map(g => {
                      if (g.id === confirmDelete.groupId) {
                        return { ...g, slots: g.slots.filter(s => s.id !== confirmDelete.slotId) };
                      }
                      return g;
                    }));
                  }
                  setConfirmDelete(null);
                }}
                className="w-full py-4 bg-red-500 text-white rounded-2xl text-[15px] font-black shadow-lg shadow-red-500/20 active:scale-95 transition-all"
              >
                确认删除
              </button>
              <button 
                onClick={() => setConfirmDelete(null)}
                className="w-full py-4 bg-gray-50 text-gray-400 rounded-2xl text-[15px] font-black active:bg-gray-100 transition-all"
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsView;
